import { Link } from "react-router";
import { ArrowDown, ArrowUp, Trash2 } from "lucide-react";

import { formatDate, formatMoney } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { effectiveScore, scoreTone } from "@/lib/workadvisory-score";
import { registrationDeadline } from "@/lib/workadvisory-period";
import { EntityCompetitionPanel } from "@/components/admin/EntityCompetitionPanel";
import { WorkAnalysisPanel } from "@/components/admin/WorkAnalysisPanel";
import type { ReportedWork, WorkVerdict } from "@/types/workadvisory";

const VERDICTS: Array<{ value: WorkVerdict; label: string; tone: string }> = [
  {
    value: "CALIFICA",
    label: "Califica",
    tone: "bg-emerald-600 text-white border-emerald-600",
  },
  {
    value: "REVISAR",
    label: "Revisar",
    tone: "bg-amber-500 text-white border-amber-500",
  },
  {
    value: "NO_CALIFICA",
    label: "No califica",
    tone: "bg-slate-600 text-white border-slate-600",
  },
];

const field =
  "w-full rounded-md border bg-background px-2.5 py-1.5 text-sm outline-none focus:border-primary";

/**
 * Una obra en la pantalla de revisión: arriba lo que trajo la máquina (no se
 * toca), abajo lo que decide la persona. El botón de mover cambia según de qué
 * lista venga — incluirla al informe o sacarla a las candidatas.
 */
export function WorkAdvisoryWorkEditor({
  work,
  organizationId,
  moveLabel,
  moveIcon,
  onChange,
  onMove,
  onRemove,
  disabled,
  preparing,
}: {
  work: ReportedWork;
  organizationId: string;
  moveLabel: string;
  moveIcon: "up" | "down";
  /** El lote del informe está corriendo. */
  preparing?: boolean;
  onChange: (patch: Partial<ReportedWork>) => void;
  onMove: () => void;
  onRemove: () => void;
  disabled?: boolean;
}) {
  const MoveIcon = moveIcon === "up" ? ArrowUp : ArrowDown;
  const deadline = registrationDeadline(work.registrationEndsAt);

  return (
    <div className="rounded-xl border bg-card p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/contracting/${work.contractingId}`}
              target="_blank"
              className="font-mono text-xs font-semibold text-primary hover:underline"
            >
              {work.nomenclature}
            </Link>
            <span className="text-[11px] font-semibold uppercase text-muted-foreground">
              {work.entity}
            </span>
            {work.workType && (
              <span
                className={cn(
                  "rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide",
                  scoreTone(work.workTypeScore),
                )}
              >
                IA: {work.workType} · score {work.workTypeScore}
              </span>
            )}
          </div>
          <p className="mt-1 text-sm leading-snug">{work.description}</p>
          {/* La entidad puede convocar desde otro departamento: si la IA leyó uno
              distinto en la descripción, se dice, porque es con el que se puntúa
              el acceso —y porque el filtro duro corrió sobre el otro—. */}
          {work.executionDepartment &&
            work.executionDepartment !== work.department && (
              <p
                className={cn(
                  "mt-1 text-[11px] font-bold",
                  work.executionInExcludedZone
                    ? "text-red-600"
                    : "text-amber-600",
                )}
              >
                {work.executionInExcludedZone ? "⚠ " : ""}
                Se ejecuta en {work.executionDepartment}, no en{" "}
                {work.department ?? "—"}
                {work.executionInExcludedZone &&
                  " · es una zona que la empresa descarta"}
              </p>
            )}
          <p className="mt-1 text-xs text-muted-foreground">
            {work.department ?? "—"} · {work.province ?? "—"} ·{" "}
            {formatDate(work.publicatedAt)} ·{" "}
            {work.amount
              ? formatMoney(work.amount, "S/ ")
              : work.declaredAmount
                ? `${formatMoney(work.declaredAmount, "S/ ")} según las bases`
                : "sin monto publicado"}
            {work.registrationEndsAt &&
              ` · registro hasta ${formatDate(work.registrationEndsAt)}`}
            {deadline && (
              <>
                {" · "}
                <span
                  className={cn(
                    "font-bold",
                    deadline.urgent ? "text-red-600" : "text-foreground",
                  )}
                >
                  {deadline.label}
                </span>
              </>
            )}
          </p>
          {/* El desglose queda a la vista: al afinar hay que saber si falló el
              score de tipo de obra o el acceso — y con qué sustento los puso la IA. */}
          {/* Sin sustento, el puntaje salió de la tabla y no de la IA — lo que
              suele significar que no logró ubicar la obra. */}
          <p className="mt-1 max-w-3xl text-[11px] italic text-muted-foreground">
            Acceso:{" "}
            {work.accessReason ?? "criterio interno (la IA no ubicó la obra)"}
          </p>
          <p className="mt-1 text-[11px] text-muted-foreground">
            score {work.workTypeScore ?? "—"} · acceso {work.departmentScore} ={" "}
            <span
              className={cn(
                "rounded px-1.5 py-0.5 font-bold",
                scoreTone(effectiveScore(work)),
              )}
            >
              {effectiveScore(work) ?? "—"}
            </span>
            {work.score !== null && " (ajustado a mano)"}
          </p>
        </div>

        <div className="flex shrink-0 gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={onMove}
            disabled={disabled}
            className="gap-1.5"
          >
            <MoveIcon className="size-3.5" />
            {moveLabel}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onRemove}
            disabled={disabled}
            title="Sacar del informe"
          >
            <Trash2 className="size-3.5" />
          </Button>
        </div>
      </div>

      <div className="mt-3 grid gap-3 border-t pt-3 md:grid-cols-[auto_1fr]">
        <div className="flex flex-wrap items-start gap-2">
          {VERDICTS.map((option) => (
            <button
              key={option.value}
              type="button"
              disabled={disabled}
              onClick={() =>
                onChange({
                  verdict:
                    work.verdict === option.value ? null : option.value,
                  // Un requisito duro incumplido anula el score: un "82 pero no
                  // califica" mata la credibilidad en una reunión.
                  ...(option.value === "NO_CALIFICA" ? { score: null } : {}),
                })
              }
              className={cn(
                "rounded-full border px-3 py-1 text-xs font-bold transition-colors",
                work.verdict === option.value
                  ? option.tone
                  : "text-muted-foreground hover:bg-accent",
              )}
            >
              {option.label}
            </button>
          ))}
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={0}
              max={100}
              disabled={disabled || work.verdict === "NO_CALIFICA"}
              value={work.score ?? ""}
              onChange={(event) =>
                onChange({
                  score:
                    event.target.value === ""
                      ? null
                      : Number(event.target.value),
                })
              }
              placeholder={
                work.suggestedScore === null
                  ? "Score"
                  : `${work.suggestedScore} sug.`
              }
              className={cn(field, "w-28 disabled:opacity-50")}
            />
            {work.score !== null && (
              <button
                type="button"
                disabled={disabled}
                onClick={() => onChange({ score: null })}
                className="text-[10px] font-semibold uppercase text-muted-foreground hover:text-foreground"
                title="Volver al score sugerido"
              >
                Usar sug.
              </button>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <textarea
            rows={2}
            disabled={disabled}
            value={work.reasons.join("\n")}
            onChange={(event) =>
              onChange({
                reasons: event.target.value
                  .split("\n")
                  .map((line) => line.trim())
                  .filter(Boolean),
              })
            }
            placeholder="Por qué se la recomendamos — una razón por línea. Lo lee el cliente."
            className={cn(field, "resize-y")}
          />
          <div className="grid gap-2 sm:grid-cols-2">
            <input
              disabled={disabled}
              value={work.districtOverride ?? ""}
              onChange={(event) =>
                onChange({ districtOverride: event.target.value || null })
              }
              placeholder={`Distrito de ejecución real (dice: ${work.district ?? "—"})`}
              className={field}
            />
            <input
              disabled={disabled}
              value={work.note ?? ""}
              onChange={(event) =>
                onChange({ note: event.target.value || null })
              }
              placeholder="Nota interna (no sale en el informe)"
              className={field}
            />
          </div>
        </div>
      </div>

      <WorkAnalysisPanel
        contractingId={work.contractingId}
        organizationId={organizationId}
        preparing={preparing}
        onAnalyzed={(frozen) =>
          onChange({
            analysis: frozen,
            // El veredicto de la IA solo precarga el vacío: lo que ya escribió
            // una persona manda sobre lo que diga el cruce.
            ...(work.verdict ? {} : { verdict: frozen.verdict }),
          })
        }
      />
      <EntityCompetitionPanel contractingId={work.contractingId} />
    </div>
  );
}
